import { useEffect, useRef, useState } from 'react'
import { scrollChildHorizontallyInContainer } from '../../utils/horizontalScrollContainer'

export enum UnderlineTabsLayout {
    STRETCH = 'stretch',
    ADAPTIVE = 'adaptive',
    FIXED = 'fixed'
}

export type UnderlineTab<T extends string> =
    | { value: T; label: string }
    | { value: T; render: (params: { isActive: boolean }) => React.ReactNode }

export type UnderlineTabsProps<T extends string> = {
    tabs: UnderlineTab<T>[]
    activeTab: T
    onChange: (tab: T) => void
    layout?: UnderlineTabsLayout
    tabWidth?: number
    gap?: number
    indicatorWidthRatio?: number
    className?: string
    rowClassName?: string
}

const DEFAULT_BAR_CLASS = 'h-10 border-b'
const DEFAULT_BORDER_COLOR_CLASS = 'border-[#E5E9EC]'

export function UnderlineTabs<T extends string>({
    tabs,
    activeTab,
    onChange,
    layout = UnderlineTabsLayout.STRETCH,
    tabWidth = 0,
    gap = 0,
    indicatorWidthRatio = 0.6,
    className,
    rowClassName
}: UnderlineTabsProps<T>) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const tabRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const [containerWidth, setContainerWidth] = useState(0)

    const activeIndex = tabs.findIndex((tab) => tab.value === activeTab)
    const totalTabsWidth = tabs.length * tabWidth + Math.max(tabs.length - 1, 0) * gap
    const adaptiveFits =
        layout === UnderlineTabsLayout.ADAPTIVE && containerWidth > 0 && totalTabsWidth <= containerWidth
    const isScrollable =
        layout === UnderlineTabsLayout.FIXED ||
        (layout === UnderlineTabsLayout.ADAPTIVE && !adaptiveFits)

    useEffect(() => {
        if (layout !== UnderlineTabsLayout.ADAPTIVE || !containerRef.current) {
            return
        }

        const container = containerRef.current
        const updateWidth = () => {
            setContainerWidth(container.clientWidth)
        }
        updateWidth()

        const resizeObserver = new ResizeObserver(updateWidth)
        resizeObserver.observe(container)
        return () => {
            resizeObserver.disconnect()
        }
    }, [layout])

    useEffect(() => {
        if (!isScrollable || !containerRef.current) {
            return
        }
        const activeTabElement = tabRefs.current[activeTab]
        if (!activeTabElement) {
            return
        }
        scrollChildHorizontallyInContainer(containerRef.current, activeTabElement, {
            alignment: 'center',
            behavior: 'smooth'
        })
    }, [activeTab, isScrollable])

    const indicatorStyle = getIndicatorStyle({
        isScrollable,
        activeIndex,
        tabCount: tabs.length,
        tabWidth,
        gap,
        indicatorWidthRatio
    })

    const tabRow = (
        <div
            className={`relative flex h-full ${rowClassName ?? ''}`}
            style={{ columnGap: `${gap}px` }}
        >
            {tabs.map((tab) => {
                const isActive = activeTab === tab.value
                return (
                    <div
                        key={tab.value}
                        ref={
                            isScrollable
                                ? (element) => {
                                      tabRefs.current[tab.value] = element
                                  }
                                : undefined
                        }
                        className={
                            isScrollable
                                ? 'flex shrink-0 cursor-pointer flex-col items-center justify-end'
                                : 'flex flex-1 cursor-pointer flex-col items-center justify-end'
                        }
                        style={isScrollable ? { width: `${tabWidth}px` } : undefined}
                        onClick={() => onChange(tab.value)}
                    >
                        {renderTab(tab, isActive)}
                    </div>
                )
            })}
            <div
                className="absolute bottom-0 h-[2px] bg-[#232323] transition-[left] duration-200 ease-out"
                style={indicatorStyle}
            />
        </div>
    )

    // className replaces default border color when provided (Tailwind can't override by string order).
    const barClassName = `${DEFAULT_BAR_CLASS} ${className ?? DEFAULT_BORDER_COLOR_CLASS}${isScrollable ? ' overflow-x-auto no-scrollbar' : ''}`

    return (
        <div ref={containerRef} className={barClassName}>
            {isScrollable ? <div className="h-full w-max min-w-full">{tabRow}</div> : tabRow}
        </div>
    )
}

function renderTab<T extends string>(tab: UnderlineTab<T>, isActive: boolean) {
    if ('render' in tab) {
        return tab.render({ isActive })
    }

    return (
        <span
            className={`pb-3 text-[14px] ${isActive ? 'font-medium text-[#232323]' : 'text-[#6A6E6E]'}`}
        >
            {tab.label}
        </span>
    )
}

function getIndicatorStyle({
    isScrollable,
    activeIndex,
    tabCount,
    tabWidth,
    gap,
    indicatorWidthRatio
}: {
    isScrollable: boolean
    activeIndex: number
    tabCount: number
    tabWidth: number
    gap: number
    indicatorWidthRatio: number
}) {
    const safeActiveIndex = activeIndex < 0 ? 0 : activeIndex

    if (isScrollable) {
        const width = `${tabWidth * indicatorWidthRatio}px`
        const leftPx = safeActiveIndex * (tabWidth + gap) + (tabWidth * (1 - indicatorWidthRatio)) / 2
        return { left: `${leftPx}px`, width }
    }

    if (tabCount <= 0) {
        return { left: '0%', width: '0%' }
    }

    const tabBasis = `(100% - ${(tabCount - 1) * gap}px) / ${tabCount}`
    return {
        left: `calc((${tabBasis} + ${gap}px) * ${safeActiveIndex} + ((${tabBasis}) * ${(1 - indicatorWidthRatio) / 2}))`,
        width: `calc((${tabBasis}) * ${indicatorWidthRatio})`
    }
}
